/**
 * The "Delete a vault by hand" guide's provider-note table: the same
 * `vaultDeleteProviderNote` table the desktop app's Delete dialog will read,
 * laid out as a page so app and site cannot say different things about the
 * same provider.
 *
 * Those sentences describe **the app's own Delete**, and this table sits at
 * the foot of a guide about deleting by hand — the one place they are not
 * interchangeable. Read unlabelled, three rows flatly contradict the section
 * they link to: the app's Drive delete really does bypass Trash, but the
 * guide's Drive section tells you to go empty the Trash; the app really does
 * delete every S3 version, but the guide's S3 section explains that a delete
 * by name leaves them all. So the heading and the lead-in say whose delete
 * this is, and the column is "What the app leaves behind", not a bare
 * "Recoverability" that reads as a claim about whatever the reader just did.
 *
 * One row per case the shared table actually distinguishes, not one per
 * provider the app supports — AWS, GCS and Azure get a single "standard
 * class" row because `vaultDeleteProviderNote` gives them the same note,
 * and a second or third row repeating it would just be the same sentence
 * shown three times.
 */

import { type DestinationKind, vaultDeleteProviderNote } from "@bigbear/shared";

interface Row {
  readonly label: string;
  readonly kind: DestinationKind;
  readonly endpointHost?: string;
}

const ROWS: Row[] = [
  { label: "A drive or network share", kind: "local" },
  { label: "Google Drive", kind: "google-drive" },
  { label: "OneDrive", kind: "onedrive" },
  { label: "Dropbox", kind: "dropbox" },
  { label: "Backblaze B2", kind: "s3", endpointHost: "s3.us-west-004.backblazeb2.com" },
  { label: "Wasabi", kind: "s3", endpointHost: "s3.wasabisys.com" },
  {
    label: "AWS S3, Google Cloud Storage or Azure Blob (standard class)",
    kind: "s3",
    endpointHost: "s3.amazonaws.com",
  },
  { label: "Another S3-compatible provider", kind: "s3" },
];

export function DeleteVaultProviderNoteTable() {
  return (
    <section className="panel" id="provider-notes" data-testid="provider-note-table">
      <h2>What the app&rsquo;s own Delete costs and leaves behind</h2>
      <p>
        These are the notes the app shows in its <strong>Delete</strong> dialog, for when you can
        still reach the vault from the PC that set it up. Deleting by hand at the provider is not
        the same thing and does not always leave the same result — each row links to the section
        above that says what to do there instead.
      </p>
      <table>
        <thead>
          <tr>
            <th scope="col">Provider</th>
            <th scope="col">Billing</th>
            <th scope="col">What the app leaves behind</th>
          </tr>
        </thead>
        <tbody>
          {ROWS.map((row) => {
            const note = vaultDeleteProviderNote(row.kind, row.endpointHost);
            return (
              <tr key={row.label}>
                <td>
                  <a href={`#${note.guideAnchor}`}>{row.label}</a>
                </td>
                <td>{note.retention}</td>
                <td>{note.recoverability}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}
